import { Locale } from "@/Common/Models/Localization/Locale";
import { GestationalAgeComponent } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/GestationalAgeComponent";
import { ObstetricsCalculator } from "@/Patient/Helpers/ObstetricsCalculator";
import { BritishColumbiaObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/BritishColumbia/BritishColumbiaObstetricalFileForm";
import { BritishColumbiaObstetricalFileUltrasound } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Part2/BritishColumbiaObstetricalFileUltrasound";
import { ConfirmedEstimatedDateOfDeliveryBy } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Part2/ConfirmedEstimatedDateOfDeliveryBy";
import { BaseBritishColumbiaObstetricalFileSubSection, BaseBritishColumbiaObstetricalFileSubSectionProps } from "../../../Subcomponent/BaseBritishColumbiaObstetricalFileSubSection";

class BritishColumbiaObstetricalFileUltrasoundState {
    public ultrasounds: BritishColumbiaObstetricalFileUltrasound[];
    public currentDate: Date;
}

class BritishColumbiaObstetricalFileSection15 extends BaseBritishColumbiaObstetricalFileSubSection<BaseBritishColumbiaObstetricalFileSubSectionProps, BritishColumbiaObstetricalFileUltrasoundState> {
    public componentWillMount(): void {
        super.componentWillMount();
        this.initState(this.props);
    }

    public render(): JSX.Element {
        return (
            <div className="section border border-bottom">
                {this.renderTitle(15, "UltrasoundAndOtherImagingInvestigationsLabel")}
                <table>
                    <thead>
                        <tr>
                            <th rowSpan={2} className="bold col-medium">{this.getString("DateLabel")}</th>
                            <th rowSpan={2} className="bold col-small">{this.getString("GestationAgeAbbreviationLabel")}</th>
                            <th rowSpan={2} className="bold col-small ultrasound-border-none-right">{this.getString("ObstetricalFile_GestationalAgeByUltrasoundAbbrevLabel")}</th>
                            <th rowSpan={2} className="col-medium" />
                        </tr>
                    </thead>
                    {this.renderUltrasoundTableBody()}
                </table>
            </div>
        );
    }

    private initState(props: BaseBritishColumbiaObstetricalFileSubSectionProps): void {
        this.mutateState(
            s => {
                s.ultrasounds = props.form.Section15?.map(p => new BritishColumbiaObstetricalFileUltrasound(p)).filter(p => p.hasValue()) ?? [];
                s.ultrasounds.push(new BritishColumbiaObstetricalFileUltrasound());
                s.currentDate = new Date();
            }
        );
    }

    private renderUltrasoundTableBody(): JSX.Element {
        return (
            <tbody>
                {this.state.ultrasounds?.map((ultrasound, index) => this.renderUltrasoundRow(ultrasound, index))}
            </tbody>
        );
    }

    private renderUltrasoundRow(ultrasound: BritishColumbiaObstetricalFileUltrasound, index: number): JSX.Element[] {
        if (!ultrasound)
            return null;

        const firstRow = (
            <tr key={`bc-ultrasound-row-${ultrasound.getId()}-1`} className="max-height-none ultrasound-table-body-row">
                <td>
                    {this.controlRenderer.renderDateSelector({
                        value: ultrasound.Date,
                        onChange: date => this.mutateUltrasoundRowState(ultrasound.getId(), p => p.Date = date, true),
                        id: `Bc_Ultrasound_${ultrasound.getId()}`,
                        className: "table-date-selector",
                        maxDate: this.state.currentDate,
                        ignoreInvalidCharacters: true,
                        containerClassName: "flex-justify-end"
                    })}
                </td>
                <td>
                    <GestationalAgeComponent
                        id={`GestationWeeksAtBirth_${ultrasound.getId()}`}
                        gestationalAge={ultrasound.GestationalAge}
                        readonly={this.props.readonly}
                        onChange={value => this.mutateUltrasoundRowState(ultrasound.getId(), p => p.GestationalAge = value)}
                        locale={Locale.en}
                        controlRenderer={this.controlRenderer}
                    />
                </td>
                <td className="ultrasound-border-none-right">
                    <GestationalAgeComponent
                        id={`UltrasoundAge_${ultrasound.getId()}`}
                        gestationalAge={ultrasound.GestationalAgeByUltrasound}
                        readonly={this.props.readonly}
                        onChange={value => this.mutateUltrasoundRowState(ultrasound.getId(), p => {
                            p.GestationalAgeByUltrasound = value;
                            if (!p.GestationalAgeByUltrasound.hasValue())
                                p.UseForEstimatedDateOfBirth = false;
                        })}
                        locale={Locale.en}
                        controlRenderer={this.controlRenderer}
                    />
                </td>
                <td className="multiple-inline-fields ultrasound-use-for-edd">
                    {this.controlRenderer.renderRadioButtonWithLabel(
                        `Ultrasouns_UseForEDD_${ultrasound.getId()}`,
                        "UseForEstimatedDateOfDelivery",
                        this.getString("UseForEstimatedDateOfDeliveryAbbrevLabel"),
                        ultrasound.UseForEstimatedDateOfBirth,
                        this.props.readonly || ultrasound.isUseForEstimatedDateOfBirthDisabled(),
                        () => this.handleUseForDating(ultrasound.getId(), !ultrasound.UseForEstimatedDateOfBirth),
                        false,
                        "",
                        "margin-none-bottom margin-xs-left padding-xs-top"
                    )}
                </td>
            </tr>
        );

        const secondRow = (
            <tr key={`bc-ultrasound-row-${ultrasound.getId()}-2`} className="resizable max-height-none">
                <td colSpan={4}>
                    {this.renderField({
                        customLabel: `${this.getString("CommentsLabel")}:`,
                        labelClassName: "flex-start-align-self normal-text margin-xs-right ultrasound-comment-label",
                        className: "width-100",
                        field: this.controlRenderer.renderResizableTextarea({
                            value: ultrasound.Comment,
                            onChange: value => this.mutateUltrasoundRowState(ultrasound.getId(), p => p.Comment = value),
                            id: `ultrasound_comment_${ultrasound.getId()}_${index}`,
                            minHeight: "32px",
                            maxHeight: "500px",
                            className: "table-textarea"
                        }),
                        isInlineFlex: true
                    })}
                </td>
            </tr>
        );

        return [firstRow, secondRow];
    }

    private handleUseForDating(ultrasoundId: number, useForDating: boolean): void {
        this.mutateState(
            s => s.ultrasounds = s.ultrasounds.mutate(p => p.UseForEstimatedDateOfBirth = p.getId() === ultrasoundId ? useForDating : false),
            () => this.mutateUltrasoundRowState(ultrasoundId, () => { }, true)
        );
    }

    private mutateUltrasoundRowState(id: number, mutation: (state: BritishColumbiaObstetricalFileUltrasound) => void, updateForm: boolean = false): void {
        this.mutateState(
            s => {
                let newRows = [...s.ultrasounds];
                newRows = newRows.mutate(
                    p => mutation(p),
                    p => p.getId() === id
                );

                let lastRowIndex = newRows.length - 1;

                for (let i = 0; i < lastRowIndex && newRows.any(); i++) {
                    if (newRows[i].hasValue())
                        continue;

                    updateForm = true;
                    newRows.splice(i, 1);
                    i--;
                    lastRowIndex--;
                }

                if (newRows[lastRowIndex].hasValue())
                    newRows.push(new BritishColumbiaObstetricalFileUltrasound());

                s.ultrasounds = newRows;
                return newRows;
            },
            () => {
                (updateForm
                    ? this.props.mutateModelStateWithFormUpdate
                    : this.props.mutateModelState
                )(s => {
                    s.Section15 = this.state.ultrasounds;
                    this.updateEstimatedDateOfBirthBasedOnUltrasound(s, this.state.ultrasounds.firstOrDefault(p => p.getId() === id));
                });
            }
        );
    }

    private updateEstimatedDateOfBirthBasedOnUltrasound(form: BritishColumbiaObstetricalFileForm, ultrasound: BritishColumbiaObstetricalFileUltrasound): void {
        if (!ultrasound?.UseForEstimatedDateOfBirth)
            return;

        const patientDateOfBirth = this.props.patient.IndividualResponse.DateOfBirth;
        const estimatedDateOfBirth = ObstetricsCalculator.calculateDateOfBirthBasedOnUltrasound(ultrasound.Date, ultrasound.GestationalAgeByUltrasound.TotalNumberOfDays);

        form.Section3.EstimatedDateOfBirthByUltrasound = estimatedDateOfBirth;
        form.Section3.DatingUltrasound = ultrasound.Date;
        form.Section3.GestationalAgeByUltrasound = ultrasound.GestationalAgeByUltrasound;

        form.Section12.ConfirmedEstimatedDateOfDelivery = estimatedDateOfBirth;
        form.Section12.ConfirmedEstimatedDateOfDeliveryBy = ConfirmedEstimatedDateOfDeliveryBy.Ultrasound;

        form.updateAgeAtEstimatedDateOfBirth(patientDateOfBirth, estimatedDateOfBirth);
    }
}

export { BritishColumbiaObstetricalFileSection15 };
